import { useEffect, useRef, useState } from "react";
import { X, Settings2, RefreshCw, Download, Info } from "lucide-react";
import { api, type PlanSettings } from "../api";

/** DOJ Title II deadlines as stated on the sales leaflet. */
export const COMPLIANCE_DEFAULTS: Record<PlanSettings["entity_size"], string> = {
  large: "2027-04-26",
  small: "2028-04-26",
};

const EMPTY: PlanSettings = {
  compliance_date: COMPLIANCE_DEFAULTS.large,
  archive_paths: [],
  exclude_paths: [],
  entity_size: "large",
  page_budget: null,
};

function linesToList(s: string): string[] {
  return s.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
}

/** Per-site plan settings in a right-hand drawer. Saving triggers a re-score
 *  on the server; Re-score / Refresh PDF details are explicit maintenance
 *  actions (each confirmed by the caller). */
export function PlanSettingsDrawer({
  open, siteId, domain, initial, onClose, onSaved, onRescore, onRefreshFacts,
}: {
  open: boolean;
  siteId: string;
  domain: string;
  initial: PlanSettings | null;
  onClose: () => void;
  onSaved: (s: PlanSettings) => void;
  onRescore: () => void;
  onRefreshFacts: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entitySize, setEntitySize] = useState<PlanSettings["entity_size"]>("large");
  const [customDate, setCustomDate] = useState(false);
  const [date, setDate] = useState(COMPLIANCE_DEFAULTS.large);
  const [archive, setArchive] = useState("");
  const [exclude, setExclude] = useState("");
  const [budget, setBudget] = useState("");

  function load(s: PlanSettings) {
    const size = s.entity_size === "small" ? "small" : "large";
    setEntitySize(size);
    const d = (s.compliance_date || "").slice(0, 10) || COMPLIANCE_DEFAULTS[size];
    setDate(d);
    setCustomDate(d !== COMPLIANCE_DEFAULTS[size]);
    setArchive((s.archive_paths || []).join("\n"));
    setExclude((s.exclude_paths || []).join("\n"));
    setBudget(s.page_budget != null ? String(s.page_budget) : "");
  }

  useEffect(() => {
    if (!open) return;
    setError(null);
    load(initial ?? EMPTY);
    let cancelled = false;
    setLoading(true);
    api.getPlanSettings(siteId)
      .then((s) => { if (!cancelled) load(s); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // `initial` intentionally not a dep — it only seeds the form on open.
  }, [open, siteId]);

  // Focus management + Escape to close; restore focus to the opener.
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    requestAnimationFrame(() => panelRef.current?.focus());
    const onKey = (e: KeyboardEvent) => {
      // When a confirm dialog is stacked on top, Escape belongs to it.
      if (e.key === "Escape" && document.querySelectorAll('[role="dialog"][aria-modal="true"]').length <= 1) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      opener?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  const effectiveDate = customDate ? date : COMPLIANCE_DEFAULTS[entitySize];

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    let pageBudget: number | null = null;
    if (budget.trim()) {
      const n = Number(budget);
      if (!Number.isInteger(n) || n < 0) { setError("Page budget must be a whole number (or leave it empty)."); return; }
      pageBudget = n;
    }
    if (customDate && !/^\d{4}-\d{2}-\d{2}$/.test(date)) { setError("Pick a valid compliance date."); return; }
    const body: PlanSettings = {
      entity_size: entitySize,
      compliance_date: effectiveDate,
      archive_paths: linesToList(archive),
      exclude_paths: linesToList(exclude),
      page_budget: pageBudget,
    };
    setBusy(true);
    try {
      const saved = await api.putPlanSettings(siteId, body);
      onSaved(saved ?? body);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-slate-900/40" role="presentation" onClick={onClose}>
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="plan-settings-title"
        className="h-full w-full max-w-md bg-white shadow-elevated flex flex-col outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <h2 id="plan-settings-title" className="text-base font-semibold text-ink flex items-center gap-2">
              <Settings2 className="h-4 w-4 text-brand-600" aria-hidden="true" /> Plan settings
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">{domain}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1 -m-1 rounded" aria-label="Close settings">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form id="plan-settings-form" onSubmit={save} className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {loading && <p className="text-xs text-slate-500" role="status">Loading saved settings…</p>}

          <fieldset>
            <legend className="text-sm font-medium text-slate-700 mb-1">Organization size</legend>
            <div className="space-y-1.5 text-sm">
              <label className="flex items-start gap-2">
                <input type="radio" name="entity_size" className="mt-1" checked={entitySize === "large"} onChange={() => setEntitySize("large")} />
                <span>Serves 50,000 people or more <span className="text-slate-500">— compliance by April 26, 2027</span></span>
              </label>
              <label className="flex items-start gap-2">
                <input type="radio" name="entity_size" className="mt-1" checked={entitySize === "small"} onChange={() => setEntitySize("small")} />
                <span>Smaller, or a special district <span className="text-slate-500">— compliance by April 26, 2028</span></span>
              </label>
            </div>
            <p className="mt-2 text-xs text-slate-500 flex items-start gap-1.5">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
              <span>Based on the DOJ ADA Title II web rule. Not legal advice — recommendations only. Documents posted or updated after this date rank higher.</span>
            </p>
          </fieldset>

          <div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={customDate} onChange={(e) => { setCustomDate(e.target.checked); if (e.target.checked && !date) setDate(COMPLIANCE_DEFAULTS[entitySize]); }} />
              Use a custom compliance date
            </label>
            {customDate ? (
              <div className="mt-2">
                <label htmlFor="plan-compliance-date" className="block text-xs font-medium text-slate-600 mb-1">Compliance date</label>
                <input id="plan-compliance-date" type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} required />
              </div>
            ) : (
              <p className="mt-1 text-xs text-slate-500">Using {new Date(`${effectiveDate}T00:00:00`).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}.</p>
            )}
          </div>

          <div>
            <label htmlFor="plan-archive-paths" className="block text-sm font-medium text-slate-700 mb-1">Archive paths</label>
            <textarea
              id="plan-archive-paths" className="input font-mono text-xs" rows={3}
              value={archive} onChange={(e) => setArchive(e.target.value)}
              placeholder="/archive/"
              aria-describedby="plan-archive-help"
            />
            <p id="plan-archive-help" className="mt-1 text-xs text-slate-500">One per line. PDFs whose URL path contains one of these are treated as archived content.</p>
          </div>

          <div>
            <label htmlFor="plan-exclude-paths" className="block text-sm font-medium text-slate-700 mb-1">Excluded paths</label>
            <textarea
              id="plan-exclude-paths" className="input font-mono text-xs" rows={3}
              value={exclude} onChange={(e) => setExclude(e.target.value)}
              placeholder="/private/"
              aria-describedby="plan-exclude-help"
            />
            <p id="plan-exclude-help" className="mt-1 text-xs text-slate-500">One per line. Matching PDFs get priority Never (private uploads are always excluded).</p>
          </div>

          <div>
            <label htmlFor="plan-page-budget" className="block text-sm font-medium text-slate-700 mb-1">Page budget</label>
            <input
              id="plan-page-budget" type="number" min={0} step={1} inputMode="numeric" className="input"
              value={budget} onChange={(e) => setBudget(e.target.value)}
              aria-describedby="plan-budget-help"
            />
            <p id="plan-budget-help" className="mt-1 text-xs text-slate-500">Optional. Pre-fills the budget planner; nothing converts automatically.</p>
          </div>

          {error && <div role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded">{error}</div>}

          <section className="border-t border-slate-200 pt-4 space-y-3" aria-labelledby="plan-maint-title">
            <h3 id="plan-maint-title" className="text-sm font-semibold text-slate-700">Maintenance</h3>
            <div>
              <button type="button" className="btn-secondary" onClick={onRescore} disabled={busy}>
                <RefreshCw className="h-4 w-4" aria-hidden="true" /> Re-score
              </button>
              <p className="mt-1 text-xs text-slate-500">Recomputes every priority from the details already collected. Quick; no downloads.</p>
            </div>
            <div>
              <button type="button" className="btn-secondary" onClick={onRefreshFacts} disabled={busy}>
                <Download className="h-4 w-4" aria-hidden="true" /> Refresh PDF details (re-download)
              </button>
              <p className="mt-1 text-xs text-slate-500">Downloads every PDF again to re-read its title, dates, forms and text. Throttled, so it can take a while on large sites.</p>
            </div>
          </section>
        </form>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" form="plan-settings-form" className="btn-primary" disabled={busy}>
            {busy ? (<><span className="spinner" aria-hidden="true" /> Saving…</>) : "Save & re-score"}
          </button>
        </div>
      </div>
    </div>
  );
}
